/**
 * 프로그램 편집 워크스페이스 IndexedDB 저장소.
 * LocalStorage(cp:program-workspace:v1) 대신 편제(scope)별로 분리 저장해 용량 한도를 피한다.
 */

import type { ProgramFixedContentRowUpsert } from '../types/domain';
import type { ProgramWorkspaceClipboard, ProgramWorkspaceScopeEntry } from './programWorkspaceTypes';

const DB_NAME = 'ClassPlannerProgramWorkspace';
const DB_VERSION = 1;
const STORE_SCOPES = 'scopes';
const STORE_CLIPBOARD = 'clipboard';
const STORE_META = 'meta';

export const PROGRAM_WORKSPACE_LS_KEY = 'cp:program-workspace:v1';
export const PROGRAM_WORKSPACE_IDB_MIGRATED_META = 'ls_migrated_v2';

type StoreName = typeof STORE_SCOPES | typeof STORE_CLIPBOARD | typeof STORE_META;

function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_SCOPES)) {
        db.createObjectStore(STORE_SCOPES);
      }
      if (!db.objectStoreNames.contains(STORE_CLIPBOARD)) {
        db.createObjectStore(STORE_CLIPBOARD);
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbTx<T>(
  store: StoreName,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const db = await idbOpen();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const req = run(tx.objectStore(store));
      req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
      req.onsuccess = () => resolve(req.result as T);
    });
  } finally {
    db.close();
  }
}

async function idbGet<T>(store: StoreName, key: string): Promise<T | undefined> {
  try {
    return await idbTx<T | undefined>(store, 'readonly', (s) => s.get(key));
  } catch {
    return undefined;
  }
}

async function idbPut(store: StoreName, key: string, value: unknown): Promise<void> {
  try {
    await idbTx(store, 'readwrite', (s) => s.put(value, key));
  } catch {
    /* quota / private mode */
  }
}

async function idbDelete(store: StoreName, key: string): Promise<void> {
  try {
    await idbTx(store, 'readwrite', (s) => s.delete(key));
  } catch {
    /* ignore */
  }
}

export async function idbGetProgramWorkspaceMeta<T>(key: string): Promise<T | undefined> {
  return idbGet<T>(STORE_META, key);
}

export async function idbPutProgramWorkspaceMeta(key: string, value: unknown): Promise<void> {
  await idbPut(STORE_META, key, value);
}

export async function idbGetProgramWorkspaceScope(
  scopeId: string
): Promise<ProgramWorkspaceScopeEntry | undefined> {
  return idbGet<ProgramWorkspaceScopeEntry>(STORE_SCOPES, scopeId);
}

export async function idbPutProgramWorkspaceScope(
  scopeId: string,
  entry: ProgramWorkspaceScopeEntry
): Promise<void> {
  await idbPut(STORE_SCOPES, scopeId, entry);
}

export async function idbDeleteProgramWorkspaceScope(scopeId: string): Promise<void> {
  await idbDelete(STORE_SCOPES, scopeId);
}

export async function idbGetAllProgramWorkspaceScopes(): Promise<ProgramWorkspaceScopeEntry[]> {
  const db = await idbOpen();
  try {
    return await new Promise<ProgramWorkspaceScopeEntry[]>((resolve, reject) => {
      const tx = db.transaction(STORE_SCOPES, 'readonly');
      const store = tx.objectStore(STORE_SCOPES);
      const req = store.getAll();
      req.onerror = () => reject(req.error ?? new Error('indexedDB getAll failed'));
      req.onsuccess = () => resolve((req.result ?? []) as ProgramWorkspaceScopeEntry[]);
    });
  } catch {
    return [];
  } finally {
    db.close();
  }
}

export async function idbDeleteProgramWorkspaceScopesExcept(retainScopeIds: Set<string>): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_SCOPES, 'readwrite');
      const store = tx.objectStore(STORE_SCOPES);
      const req = store.getAllKeys();
      req.onerror = () => reject(req.error ?? new Error('indexedDB getAllKeys failed'));
      req.onsuccess = () => {
        const keys = (req.result ?? []) as string[];
        keys.forEach((key) => {
          if (!retainScopeIds.has(key)) store.delete(key);
        });
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB tx failed'));
    });
  } catch {
    /* ignore */
  } finally {
    db.close();
  }
}

export async function idbGetProgramWorkspaceClipboard(): Promise<ProgramWorkspaceClipboard | null | undefined> {
  return idbGet<ProgramWorkspaceClipboard | null>(STORE_CLIPBOARD, 'v1');
}

export async function idbPutProgramWorkspaceClipboard(
  clipboard: ProgramWorkspaceClipboard | null
): Promise<void> {
  await idbPut(STORE_CLIPBOARD, 'v1', clipboard);
}

export async function idbClearProgramWorkspaceClipboard(): Promise<void> {
  await idbDelete(STORE_CLIPBOARD, 'v1');
}

/** LocalStorage v1 → IndexedDB 일회성 이전 */
export async function migrateProgramWorkspaceFromLocalStorageIfNeeded(): Promise<void> {
  if (typeof window === 'undefined') return;
  const migrated = await idbGetProgramWorkspaceMeta<boolean>(PROGRAM_WORKSPACE_IDB_MIGRATED_META);
  if (migrated) return;

  try {
    const raw = window.localStorage.getItem(PROGRAM_WORKSPACE_LS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as {
        scopes?: Record<string, ProgramWorkspaceScopeEntry>;
        clipboard?: ProgramWorkspaceClipboard | null;
      };
      const scopes = parsed?.scopes && typeof parsed.scopes === 'object' ? parsed.scopes : {};
      await Promise.all(
        Object.entries(scopes).map(([scopeId, entry]) => idbPutProgramWorkspaceScope(scopeId, entry))
      );
      if (parsed?.clipboard !== undefined) {
        await idbPutProgramWorkspaceClipboard(parsed.clipboard ?? null);
      }
      window.localStorage.removeItem(PROGRAM_WORKSPACE_LS_KEY);
    }
  } catch {
    /* LS 손상 시 IDB만 사용 */
  }

  await idbPutProgramWorkspaceMeta(PROGRAM_WORKSPACE_IDB_MIGRATED_META, true);
}

export type ProgramWorkspaceIdbSnapshot = {
  scopes: Record<string, ProgramWorkspaceScopeEntry>;
  clipboard: ProgramWorkspaceClipboard | null;
};

export async function loadProgramWorkspaceIdbSnapshot(): Promise<ProgramWorkspaceIdbSnapshot> {
  await migrateProgramWorkspaceFromLocalStorageIfNeeded();
  const entries = await idbGetAllProgramWorkspaceScopes();
  const scopes: Record<string, ProgramWorkspaceScopeEntry> = {};
  entries.forEach((entry) => {
    if (entry?.scopeId) scopes[entry.scopeId] = entry;
  });
  const clipboard = (await idbGetProgramWorkspaceClipboard()) ?? null;
  return { scopes, clipboard };
}

export async function persistProgramWorkspaceIdbSnapshot(snapshot: ProgramWorkspaceIdbSnapshot): Promise<void> {
  const scopeIds = new Set(Object.keys(snapshot.scopes));
  const existing = await idbGetAllProgramWorkspaceScopes();
  await Promise.all(
    existing
      .filter((entry) => entry.scopeId && !scopeIds.has(entry.scopeId))
      .map((entry) => idbDeleteProgramWorkspaceScope(entry.scopeId))
  );
  await Promise.all(
    Object.entries(snapshot.scopes).map(([scopeId, entry]) => idbPutProgramWorkspaceScope(scopeId, entry))
  );
  await idbPutProgramWorkspaceClipboard(snapshot.clipboard);
}

/** scope 단위 부분 저장 — 변경된 scope만 IDB에 기록 */
export async function persistProgramWorkspaceScopePartial(
  scopeId: string,
  entry: ProgramWorkspaceScopeEntry | null,
  clipboard: ProgramWorkspaceClipboard | null
): Promise<void> {
  if (entry) {
    await idbPutProgramWorkspaceScope(scopeId, entry);
  } else {
    await idbDeleteProgramWorkspaceScope(scopeId);
  }
  await idbPutProgramWorkspaceClipboard(clipboard);
}

export async function idbClearAllProgramWorkspace(): Promise<void> {
  const db = await idbOpen();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([STORE_SCOPES, STORE_CLIPBOARD, STORE_META], 'readwrite');
      tx.objectStore(STORE_SCOPES).clear();
      tx.objectStore(STORE_CLIPBOARD).clear();
      tx.objectStore(STORE_META).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB clear failed'));
    });
  } catch {
    /* ignore */
  } finally {
    db.close();
  }
}

export function rowStableKey(row: ProgramFixedContentRowUpsert, fallbackIndex: number): string {
  const id = (row.id ?? '').trim();
  if (id) return `id:${id}`;
  const ak = (row.source_annual_cell_key ?? '').trim();
  if (ak) return `ak:${ak}`;
  return `ord:${row.order_index ?? fallbackIndex}`;
}
